import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintletDashboard, fetchPrintletWallet } from "../../api/PrintletAPIs";
import {
  Wallet,
  Package,
  Truck,
  AlertTriangle,
  DollarSign,
  Clock,
  Inbox,
  RefreshCw,
} from "lucide-react";

function InfoRow({ icon: Icon, label, value, accent = "slate" }) {
  const accents = {
    indigo: "text-indigo-500",
    emerald: "text-emerald-500",
    amber: "text-amber-500",
    violet: "text-violet-500",
    slate: "text-slate-400",
  };
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Icon size={15} className={accents[accent]} />
        {label}
      </div>
      <div className="font-semibold text-slate-800">{value}</div>
    </div>
  );
}

export default function PrintletFinancialsPage() {
  const { data: dash, isLoading: dashLoading } = useQuery({
    queryKey: ["printletDashboard"],
    queryFn: () => fetchPrintletDashboard(),
  });
  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ["printletWallet"],
    queryFn: fetchPrintletWallet,
  });

  const walletItems = wallet?.items || [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Wallet size={22} className="text-indigo-600" />
          المالية
        </h2>
        <p className="text-sm text-slate-500 mt-1">ملخص COD والتكاليف ومحفظة Printlet</p>
      </div>

      {/* Period summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
            <DollarSign size={18} />
          </div>
          <h3 className="font-bold text-slate-800">ملخص الفترة الحالية</h3>
        </div>

        {dashLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 bg-slate-50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-0">
              <InfoRow
                icon={Package}
                label="COD المتوقع (سعر بيع جراباتكم المُرسل للتحصيل)"
                value={Number(dash?.codExpected ?? 0).toLocaleString()}
                accent="amber"
              />
              <InfoRow
                icon={DollarSign}
                label="تكلفة تنفيذ Printlet (printlet_total)"
                value={Number(dash?.printletFulfillmentCost ?? 0).toLocaleString()}
                accent="indigo"
              />
              <InfoRow
                icon={Truck}
                label="شحن Printlet من عرض السعر"
                value={Number(dash?.printletShippingCost ?? 0).toLocaleString()}
                accent="violet"
              />
            </div>

            <div className="flex items-start gap-2 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>
                Printlet v1 لا يوفّر API للتسويات أو المدفوعات المجمّعة. لا يمكن مطابقة settled amount تلقائياً.
              </span>
            </div>
          </>
        )}
      </div>

      {/* Wallet */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <Wallet size={18} />
          </div>
          <h3 className="font-bold text-slate-800">محفظة Printlet</h3>
          <span className="mr-auto text-xs text-slate-400 font-mono">GET /wallet</span>
        </div>

        {walletLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-16 bg-slate-50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : walletItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="p-3 rounded-full bg-slate-100 mb-2">
              <Inbox size={24} className="text-slate-400" />
            </div>
            <p className="text-sm text-slate-500">لا توجد أرصدة بعد المزامنة</p>
            <p className="text-xs text-slate-400 mt-1">بيئة الاختبار قد ترجع محفظة فارغة</p>
          </div>
        ) : (
          <div className="space-y-2">
            {walletItems.map((w) => (
              <div key={w.id} className="border border-slate-200 rounded-xl p-4 hover:border-indigo-200 transition">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-slate-800">{w.currency}</span>
                  <span className="text-xs text-slate-400 inline-flex items-center gap-1" dir="ltr">
                    <Clock size={11} />
                    {w.syncedAtUtc}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-2.5">
                    <p className="text-xs text-emerald-700 mb-0.5">متاح</p>
                    <p className="font-bold text-emerald-800">{Number(w.availableBalance || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2.5">
                    <p className="text-xs text-amber-700 mb-0.5">معلّق</p>
                    <p className="font-bold text-amber-800">{Number(w.pendingBalance || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {wallet?.note && (
          <p className="text-xs text-slate-500 mt-3 p-2.5 bg-slate-50 rounded-lg">{wallet.note}</p>
        )}
      </div>
    </div>
  );
}